import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripe";
import {
  createCardSetupIntent,
  saveCardPaymentMethod,
} from "../../api/services";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#424770",
      fontFamily:
        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#9e2146", iconColor: "#9e2146" },
  },
  hidePostalCode: false,
};

interface AddCardFormProps {
  onClose: () => void;
  onSaved: () => void;
}

const AddCardForm: React.FC<AddCardFormProps> = ({ onClose, onSaved }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const card = elements.getElement(CardElement);
      if (!card) {
        setError(t("addCardModal.errors.noCardElement"));
        setLoading(false);
        return;
      }

      const setupRes = await createCardSetupIntent();
      const clientSecret = setupRes.data.clientSecret;

      const { error: confirmError, setupIntent } =
        await stripe.confirmCardSetup(clientSecret, {
          payment_method: { card },
        });

      if (confirmError) {
        setError(confirmError.message || t("addCardModal.errors.default"));
        setLoading(false);
        return;
      }
      if (!setupIntent || typeof setupIntent.payment_method !== "string") {
        setError(t("addCardModal.errors.noSetupIntent"));
        setLoading(false);
        return;
      }

      await saveCardPaymentMethod(setupIntent.payment_method);
      onSaved();
    } catch (err: any) {
      console.error("Card save error:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          t("addCardModal.errors.default"),
      );
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mb-4">
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("addCardModal.label")}
        </label>
        <div className="rounded-lg border border-gray-300 px-4 py-4 transition-all focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          onClick={onClose}
          disabled={loading}
        >
          {t("addCardModal.cancel")}
        </button>
        <button
          type="submit"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!stripe || loading}
        >
          {loading ? t("addCardModal.saving") : t("addCardModal.save")}
        </button>
      </div>
    </form>
  );
};

interface AddCardModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const AddCardModal: React.FC<AddCardModalProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-lg">
        <div className="border-b border-gray-100 px-6 py-4">
          <h4 className="font-semibold text-primary">
            {t("addCardModal.title")}
          </h4>
        </div>
        <div className="p-6">
          <Elements stripe={stripePromise}>
            <AddCardForm onClose={onClose} onSaved={onSaved} />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default AddCardModal;
